import React, { useEffect, useState } from 'react'

import Box from 'components_new/atoms/Box'
import Drawer from 'components_new/atoms/Drawer'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import dayjs from 'utils/dayjs'
import { FileDelivery } from 'redux/selectors/internal/DataRuns'
import {
  deliveryLabel,
  deliveryStatusLabel,
  DELIVERY_STATUS_COLOR,
  formatDayClock,
  formatFileCount,
  formatIntervalHours,
  formatOverdueHours,
  isDeliveryIncomplete
} from '../utils'

/** Same width as the job detail panel, per the design handoff. */
const PANEL_WIDTH = 432

export interface DataRunsFileDeliveryPanelProps {
  /** The file source to show. `null` closes the panel. */
  delivery: FileDelivery | null
  onClose: () => void
}

interface FactCellProps {
  label: string
  value: string
}

const FactCell = (props: FactCellProps) => (
  <Box sx={{ bgcolor: 'grey.100', borderRadius: '12px', p: '12px 14px' }}>
    <Text
      sx={{
        color: 'text.secondary',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase'
      }}
    >
      {props.label}
    </Text>
    <Text
      component="div"
      sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, mt: 0.25 }}
      variant="body1"
    >
      {props.value}
    </Text>
  </Box>
)

const SectionLabel = (props: { children: string }) => (
  <Text
    sx={{
      color: 'text.secondary',
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase'
    }}
  >
    {props.children}
  </Text>
)

/**
 * Why this source does or does not have an interval - the panel's answer to
 * "so why isn't this flagged?".
 *
 * One case per `delivery_pattern`, because the three cadence-less ones are
 * genuinely different situations and a single "för få leveranser" sentence
 * was wrong for two of them.
 */
function intervalExplanation(delivery: FileDelivery): string {
  switch (delivery.delivery_pattern) {
  case 'none':
    return 'Inga leveranser i läsfönstret. Ingen försening kan bedömas.'
  case 'one_time':
    return (
      `Bara ${delivery.delivery_count} leveranser i läsfönstret. För få ` +
        'för att kalla källan återkommande, så ingen försening bedöms.'
    )
  case 'irregular':
    return (
      `${delivery.interval_sample_size} mellanrum, men utan mönster. ` +
        'Källan levererar oregelbundet och bedöms därför inte mot något ' +
        'intervall.'
    )
  default:
    return (
      `Median av ${delivery.interval_sample_size} mellanrum mellan källans ` +
        `${delivery.delivery_count} senaste leveranser. Härlett ur ` +
        'historiken, inte ur ett avtalat intervall.'
    )
  }
}

const FileDeliveryPanelContent = (props: {
  delivery: FileDelivery
  onClose: () => void
}) => {
  const { delivery, onClose } = props

  const overdue = formatOverdueHours(delivery.overdue_hours)
  const fileCount = formatFileCount(
    delivery.file_count,
    delivery.expected_file_count
  )
  const missingFileNames = delivery.missing_file_names ?? []
  const missingFilesUnknown =
    isDeliveryIncomplete(delivery) && delivery.missing_file_names === null
  const history = delivery.history ?? []

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
        height: '100%',
        overflowY: 'auto',
        p: '22px 24px 32px',
        width: PANEL_WIDTH
      }}
    >
      <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 1.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tag
            color={DELIVERY_STATUS_COLOR[delivery.status]}
            label={deliveryStatusLabel(delivery)}
          />
          <Text
            component="h2"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.04em',
              lineHeight: 1.1,
              mt: 1,
              wordBreak: 'break-word'
            }}
            variant="h5"
          >
            {deliveryLabel(delivery)}
          </Text>
          <Text sx={{ color: 'text.secondary', mt: 0.5 }} variant="body2">
            {delivery.customer?.name ?? 'Okänd kund'} · {delivery.source_name}
          </Text>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '50%',
            height: 32,
            width: 32
          }}
        >
          <Icon fontSize="small" name="Close" />
        </IconButton>
      </Box>

      {delivery.last_failure_message ? (
        <Box
          sx={{
            bgcolor: 'error.transparent',
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: '14px',
            p: '14px 16px'
          }}
        >
          <Text
            sx={{
              color: 'error.text',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase'
            }}
          >
            Senaste felmeddelande
          </Text>
          <Text sx={{ mt: 0.5, wordBreak: 'break-word' }} variant="body2">
            {delivery.last_failure_message}
          </Text>
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gap: '10px',
          gridTemplateColumns: 'repeat(2, 1fr)'
        }}
      >
        <FactCell
          label="Senaste leverans"
          value={formatDayClock(delivery.last_delivery_at)}
        />
        <FactCell
          label="Senast lyckad"
          value={formatDayClock(delivery.last_successful_delivery_at)}
        />
        <FactCell
          label="Intervall"
          value={formatIntervalHours(delivery.median_interval_hours)}
        />
        <FactCell
          label="Väntad nästa"
          value={formatDayClock(delivery.expected_next_delivery_at)}
        />
        {fileCount ? (
          <FactCell label="Filer i leveransen" value={fileCount} />
        ) : null}
      </Box>

      {missingFilesUnknown ? (
        <Text sx={{ color: 'warning.text' }} variant="body2">
          Senaste leveransen innehöll färre filer än källan brukar skicka.
          Vilken fil som saknas går inte att se — leveranshistoriken har inga
          filnamn på de raderna.
        </Text>
      ) : null}

      {missingFileNames.length > 0 ? (
        <Box>
          <Text sx={{ color: 'warning.text' }} variant="body2">
            Dessa filer kom inte in i senaste leveransen:
          </Text>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {missingFileNames.map((fileName) => (
              <Text
                component="li"
                key={fileName}
                sx={{ color: 'warning.text', wordBreak: 'break-all' }}
                variant="body2"
              >
                {fileName}
              </Text>
            ))}
          </Box>
          <Text sx={{ color: 'text.secondary', mt: 0.5 }} variant="body2">
            Jämfört med alla filnamn källan någon gång har skickat. En fil
            som källan medvetet har slutat skicka räknas därför också som
            saknad.
          </Text>
        </Box>
      ) : null}

      {overdue ? (
        <Text sx={{ color: 'warning.text' }} variant="body2">
          {overdue} jämfört med källans vanliga intervall.
        </Text>
      ) : null}

      <Box>
        <SectionLabel>Om intervallet</SectionLabel>
        <Text sx={{ color: 'text.secondary', mt: 0.5 }} variant="body2">
          {intervalExplanation(delivery)}
        </Text>
      </Box>

      {delivery.sftp_path ? (
        <Box>
          <SectionLabel>SFTP-katalog</SectionLabel>
          <Text
            sx={{
              mt: 0.5,
              userSelect: 'all',
              wordBreak: 'break-all'
            }}
            variant="body2"
          >
            s3://homepal-sftp-files/{delivery.sftp_path}/
          </Text>
        </Box>
      ) : null}

      <Box>
        <SectionLabel>Källans id</SectionLabel>
        <Text
          sx={{ mt: 0.5, userSelect: 'all', wordBreak: 'break-all' }}
          variant="body2"
        >
          {delivery.active_source_id}
        </Text>
      </Box>

      <Box>
        <SectionLabel>Leveranser, 14 dygn</SectionLabel>
        {history.length === 0 ? (
          <Text sx={{ color: 'text.disabled', mt: 0.5 }} variant="body2">
            Inga leveranser de senaste 14 dygnen.
          </Text>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              mt: 1
            }}
          >
            {history.map((entry) => (
              <Box
                key={entry.delivered_at}
                sx={{
                  alignItems: 'baseline',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 1,
                  pb: 0.75
                }}
              >
                <Box
                  sx={{
                    bgcolor:
                      entry.status === 'ok' ? 'success.main' : 'error.main',
                    borderRadius: '2px',
                    flexShrink: 0,
                    height: '8px',
                    mt: '4px',
                    width: '8px'
                  }}
                />
                <Text
                  sx={{
                    flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                    width: 96
                  }}
                  variant="body2"
                >
                  {dayjs(entry.delivered_at).tz().format('D MMM HH:mm')}
                </Text>
                <Text
                  sx={{
                    color:
                      entry.status === 'ok' ? 'text.secondary' : 'error.text',
                    wordBreak: 'break-word'
                  }}
                  variant="body2"
                >
                  {entry.message ?? '–'}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  )
}

/**
 * Detail panel for a customer file source, opened from any delivery row on
 * the Datakörningar page.
 *
 * A sibling of the job DetailPanel rather than a widening of it: that one
 * is built throughout around a Glue run (Glue Studio and CloudWatch links,
 * DPU and cost, dependency graph, duration bars) and a file delivery shares
 * none of it. If a third panel ever appears, extract a shared drawer shell -
 * today the duplication is a Drawer, a close button and one constant.
 *
 * Deliberately no deep link into Collect: that route is customer-tenant
 * scoped and will not resolve for a Homepal operator looking at another
 * customer's source. The id and the S3 prefix are selectable instead.
 *
 * Caches the last non-null delivery so the content stays rendered while the
 * Drawer's slide-out transition plays, same as the job panel.
 */
const DataRunsFileDeliveryPanel = (props: DataRunsFileDeliveryPanelProps) => {
  const { delivery, onClose } = props

  const [cachedDelivery, setCachedDelivery] = useState<FileDelivery | null>(
    delivery
  )

  useEffect(() => {
    if (delivery) {
      setCachedDelivery(delivery)
    }
  }, [delivery])

  return (
    <Drawer
      anchor="right"
      elevation={0}
      onClose={onClose}
      onMouseDown={(event) => event.stopPropagation()}
      open={!!delivery}
      PaperProps={{
        sx: { borderLeft: '1px solid', borderColor: 'divider' }
      }}
    >
      {cachedDelivery ? (
        <FileDeliveryPanelContent delivery={cachedDelivery} onClose={onClose} />
      ) : null}
    </Drawer>
  )
}

DataRunsFileDeliveryPanel.displayName = 'DataRunsFileDeliveryPanel'
export default DataRunsFileDeliveryPanel
